import { useId } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SafeText } from "@/shared/components/SafeText";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import { formatDateShort } from "@/shared/lib/format-date";
import { organizationPath } from "@/shared/organizations/organization-link";
import type { UserDetail } from "../types";
import { MembershipLocations } from "./MembershipLocations";
import { MembershipPermissions } from "./MembershipPermissions";
import { MembershipRole, type AdminMembership } from "@/shared/organizations/membership-types";

const ROLE_BADGE_VARIANT = {
  [MembershipRole.OWNER]: "default",
  [MembershipRole.ADMIN]: "default",
  [MembershipRole.STAFF]: "secondary",
} as const;

interface MembershipCardProps {
  readonly membership: AdminMembership;
  /** The page's user detail: the source of an OWNER's own venues / properties. */
  readonly user: UserDetail;
  readonly canManage: boolean;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (entityId: string) => void;
  /** Viewer may move ADMIN <-> STAFF (`org.admins.manage`). Never offered on OWNER or inactive rows. */
  readonly canChangeRole?: boolean;
  readonly onChangeRole?: (membership: AdminMembership) => void;
}

/**
 * One membership of the user: organization (with its test-partition badge),
 * role, status, how it came to exist, dates, the role's permissions
 * (collapsible) and — for STAFF — the assigned locations.
 */
export function MembershipCard({
  membership,
  user,
  canManage,
  selected,
  onToggle,
  canChangeRole = false,
  onChangeRole,
}: MembershipCardProps) {
  const { t, i18n } = useTranslation();
  const titleId = useId();
  const { id, role, status, source, organization } = membership;
  const isActive = status === "ACTIVE";
  const roleChangeable = isActive && role !== MembershipRole.OWNER;
  const since = formatDateShort(membership.createdAt, i18n.language) || "—";
  const ended = formatDateShort(membership.deactivatedAt, i18n.language);
  return (
    <li
      data-testid={`membership-${id}`}
      aria-labelledby={titleId}
      className="space-y-3 rounded-md border border-border p-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <h3 id={titleId} className="truncate text-sm font-semibold">
            <Link
              to={organizationPath(organization.id)}
              data-testid={`membership-org-link-${id}`}
              className="rounded-sm hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <SafeText fallback={t("organizations.unnamed")}>{organization.name}</SafeText>
            </Link>
          </h3>
          <TestPartitionBadge isTest={organization.isTest} testId={`membership-test-${id}`} />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">{t(`users.memberships.kind.${organization.kind}`)}</Badge>
          <Badge variant={ROLE_BADGE_VARIANT[role]} data-testid={`membership-role-${id}`}>
            {t(`users.memberships.role.${role}`)}
          </Badge>
          <Badge
            variant={isActive ? "success" : "secondary"}
            data-testid={`membership-status-${id}`}
          >
            {t(`users.memberships.status.${status}`)}
          </Badge>
          <Badge variant={source === "ADMIN" ? "warning" : "secondary"}>
            {t(`users.memberships.source.${source}`)}
          </Badge>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("users.memberships.since", { date: since })}
        {!isActive && ended ? ` · ${t("users.memberships.deactivatedOn", { date: ended })}` : ""}
        {!organization.active ? ` · ${t("users.memberships.organizationInactive")}` : ""}
      </p>
      {canChangeRole && roleChangeable && onChangeRole && (
        <Button
          data-testid={`membership-change-role-${id}`}
          variant="outline"
          size="sm"
          onClick={() => onChangeRole(membership)}
        >
          <ArrowLeftRight className="mr-2 h-4 w-4" aria-hidden="true" />
          {t("organizationAccess.changeRole.action")}
        </Button>
      )}
      <MembershipPermissions membershipId={id} permissions={membership.permissions} />
      <MembershipLocations
        membership={membership}
        user={user}
        canManage={canManage}
        selected={selected}
        onToggle={onToggle}
      />
    </li>
  );
}
